import { useCallback, useEffect, useRef, useState } from "react";
import {
  applyNodeChanges,
  Edge,
  getConnectedEdges,
  Node,
  NodeAddChange,
  NodeChange,
  NodeResetChange,
  OnNodesChange,
} from "reactflow";
import * as Y from "yjs";

const isNodeAddChange = (change: NodeChange): change is NodeAddChange => change.type === "add";
const isNodeResetChange = (change: NodeChange): change is NodeResetChange =>
  change.type === "reset";

function useNodesStateSynced(
  nodeIds?: string[],
  nodesMap?: Y.Map<Node>,
  edgesMap?: Y.Map<Edge>
): [Node[], OnNodesChange] {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [selection, setSelection] = useState<Set<string>>(new Set());

  // The onNodesChange callback updates nodesMap.
  // When the changes are applied to the map, the observer will be triggered and updates the nodes state.
  const onNodesChanges: OnNodesChange = useCallback(
    (changes) => {
      if (!nodesMap || !edgesMap) return;

      const nodes = Array.from(nodesMap.values());
      const newSelection = new Set([...selection]);
      nodesMap.doc?.transact(() => {
        const nextNodes = applyNodeChanges(changes, nodes);
        changes.forEach((change: NodeChange) => {
          if (!isNodeAddChange(change) && !isNodeResetChange(change)) {
            const node = nextNodes.find((n) => n.id === change.id);
            if (change.type == "select") {
              if (change.selected == true) {
                newSelection.add(change.id);
              } else {
                newSelection.delete(change.id);
              }
            } else if (node && change.type !== "remove") {
              nodesMap.set(change.id, node);
            } else if (change.type === "remove") {
              // Deleting disabled for now
              // const deletedNode = nodesMap.get(change.id);
              // nodesMap.delete(change.id);
              // // when a node is removed, we also need to remove the connected edges
              // const edges = Array.from(edgesMap.values()).map((e) => e);
              // const connectedEdges = getConnectedEdges(deletedNode ? [deletedNode] : [], edges);
              // connectedEdges.forEach((edge) => edgesMap.delete(edge.id));
            }
          }
        });
      });
      setSelection(newSelection);
    },
    [nodesMap, selection]
  );

  // here we are observing the nodesMap and updating the nodes state whenever the map changes.
  useEffect(() => {
    if (!nodesMap) return;
    const observer = () => {
      let nodesArr = Array.from(nodesMap.values());

      if (nodeIds) {
        nodesArr = nodesArr.filter((node) => nodeIds.includes(node.id));
      }
      const allNodeIds = nodesArr.map((node) => node.id);
      nodesArr.forEach((node) => {
        if (!allNodeIds.includes(node.parentNode) || node.parentNode == node.id) {
          node.parentNode = null;
        }
      });

      // Function to find all descendants of a given node
      function findAllDescendants(nodeId: string, nodesToRemove: Set<string>) {
        nodesArr.forEach((node) => {
          if (node.parentNode === nodeId) {
            nodesToRemove.add(node.id);
            findAllDescendants(node.id, nodesToRemove);
          }
        });
      }

      // Set to keep track of all nodes to be removed
      const nodesToRemove = new Set<string>();

      // Find all collapsed nodes and their descendants
      nodesArr.forEach((node) => {
        if (node.data.collapsed) {
          findAllDescendants(node.id, nodesToRemove);
        }
      });

      // Filter out only the descendants of collapsed nodes
      nodesArr = nodesArr.filter((node) => !nodesToRemove.has(node.id));

      // Add in selection
      nodesArr.forEach((node) => {
        node.selected = selection.has(node.id);
      });

      nodesArr = nodesArr.sort((a, b) => {
        return b.width - a.width;
      });

      setNodes(nodesArr);
    };
    observer();
    nodesMap.observe(observer);

    return () => nodesMap.unobserve(observer);
  }, [nodesMap, setNodes, nodeIds, selection]);

  return [nodes.filter((n) => n), onNodesChanges];
}

export default useNodesStateSynced;
